/**
 * Change tracking for the drafting wizard's live preview.
 *
 * The preview is a rendered .docx (docx-preview turns it into `<p>` and
 * `<span>` elements), and nothing in it says which text came from which form
 * field: a date lands formatted, a number may land as words, a boolean adds or
 * removes a whole passage. So instead of searching the document for the value
 * the user typed, the panel remembers the paragraphs of the previous render and
 * diffs the new render against them. Whatever differs is what the user's edit
 * did to the document — filters, conditionals and lists included.
 *
 * Pure DOM/array helpers, so they can be reasoned about without the panel.
 */

/** CSS classes the panel styles. Kept here so the marker and the styles agree. */
export const PREVIEW_CHANGE_CLASS = 'gaia-preview-change';
export const PREVIEW_CHANGE_ADDED_CLASS = 'gaia-preview-change--added';
export const PREVIEW_CHANGE_REMOVED_CLASS = 'gaia-preview-change--removed';
export const PREVIEW_CHANGE_CURRENT_CLASS = 'gaia-preview-change--current';
export const PREVIEW_MARK_CLASS = 'gaia-preview-mark';

/** One contiguous edit: `oldCount` paragraphs became `newCount` paragraphs. */
export interface DiffHunk {
  oldStart: number;
  oldCount: number;
  newStart: number;
  newCount: number;
}

/**
 * Above this many DP cells the exact diff is abandoned for the trimmed middle
 * as one block — a 1400-paragraph change on both sides is not something a
 * highlight can make sense of anyway.
 */
const MAX_LCS_CELLS = 2_000_000;

/**
 * Body paragraphs of the rendered document, in reading order.
 *
 * Only what sits inside a page's `<article>`: headers and footers repeat on
 * every page, so a document that grows by a page would otherwise report its
 * new header and footer as changes.
 */
export function collectBodyParagraphs(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>('article p')];
}

export function collectBodyParagraphTexts(container: HTMLElement): string[] {
  return collectBodyParagraphs(container).map(paragraph => paragraph.textContent);
}

/**
 * Sequence diff of two paragraph lists, as hunks over the changed regions.
 *
 * The unchanged head and tail are trimmed first — that is most of any
 * document — and the middle gets an exact LCS alignment, so a party name that
 * appears in five places yields five hunks rather than one span from the first
 * to the last.
 */
export function diffSequences(oldItems: string[], newItems: string[]): DiffHunk[] {
  let prefix = 0;
  while (
    prefix < oldItems.length &&
    prefix < newItems.length &&
    oldItems[prefix] === newItems[prefix]
  ) {
    prefix += 1;
  }
  let suffix = 0;
  while (
    suffix < oldItems.length - prefix &&
    suffix < newItems.length - prefix &&
    oldItems[oldItems.length - 1 - suffix] === newItems[newItems.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  const oldMiddle = oldItems.slice(prefix, oldItems.length - suffix);
  const newMiddle = newItems.slice(prefix, newItems.length - suffix);
  if (oldMiddle.length === 0 && newMiddle.length === 0) return [];
  if (oldMiddle.length * newMiddle.length > MAX_LCS_CELLS) {
    return [
      {
        oldStart: prefix,
        oldCount: oldMiddle.length,
        newStart: prefix,
        newCount: newMiddle.length,
      },
    ];
  }

  return alignMiddle(oldMiddle, newMiddle).map(hunk => ({
    ...hunk,
    oldStart: hunk.oldStart + prefix,
    newStart: hunk.newStart + prefix,
  }));
}

/** LCS table + backtrack over the trimmed middle, grouped into hunks. */
function alignMiddle(oldItems: string[], newItems: string[]): DiffHunk[] {
  const rows = oldItems.length;
  const cols = newItems.length;
  const width = cols + 1;
  // lcs[i * width + j] = LCS length of oldItems[i..] and newItems[j..].
  const lcs = new Uint16Array((rows + 1) * width);
  for (let i = rows - 1; i >= 0; i -= 1) {
    for (let j = cols - 1; j >= 0; j -= 1) {
      lcs[i * width + j] =
        oldItems[i] === newItems[j]
          ? lcs[(i + 1) * width + j + 1] + 1
          : Math.max(lcs[(i + 1) * width + j], lcs[i * width + j + 1]);
    }
  }

  const hunks: DiffHunk[] = [];
  let open: DiffHunk | null = null;
  let i = 0;
  let j = 0;
  const closeHunk = (): void => {
    if (open) hunks.push(open);
    open = null;
  };
  const extend = (oldDelta: number, newDelta: number): void => {
    open ??= { oldStart: i, oldCount: 0, newStart: j, newCount: 0 };
    open.oldCount += oldDelta;
    open.newCount += newDelta;
    i += oldDelta;
    j += newDelta;
  };

  while (i < rows || j < cols) {
    if (i < rows && j < cols && oldItems[i] === newItems[j]) {
      closeHunk();
      i += 1;
      j += 1;
    } else if (j < cols && (i >= rows || lcs[i * width + j + 1] >= lcs[(i + 1) * width + j])) {
      extend(0, 1);
    } else {
      extend(1, 0);
    }
  }
  closeHunk();
  return hunks;
}

/**
 * The characters of `next` that differ from `previous`, as one range — the
 * common head and tail are what did not change. Null when nothing in `next`
 * is new (a pure deletion), in which case the paragraph itself is the marker.
 */
export function changedCharacterRange(
  previous: string,
  next: string
): { start: number; end: number } | null {
  let start = 0;
  while (start < previous.length && start < next.length && previous[start] === next[start]) {
    start += 1;
  }
  let suffix = 0;
  while (
    suffix < previous.length - start &&
    suffix < next.length - start &&
    previous[previous.length - 1 - suffix] === next[next.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  const end = next.length - suffix;
  return end > start ? { start, end } : null;
}

/**
 * Wrap the characters `[start, end)` of a paragraph's text in `<mark>`s.
 *
 * The range can cross several text nodes (docx-preview emits one `<span>` per
 * run), so each intersecting node is split at the range boundaries and its
 * middle piece wrapped — the nodes are collected before anything is split,
 * since splitting while walking would revisit the new siblings.
 */
export function markTextRange(paragraph: HTMLElement, start: number, end: number): void {
  const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT);
  const nodes: { node: Text; offset: number }[] = [];
  let offset = 0;
  for (let current = walker.nextNode(); current; current = walker.nextNode()) {
    const node = current as Text;
    nodes.push({ node, offset });
    offset += node.length;
  }

  for (const { node, offset: nodeStart } of nodes) {
    const nodeEnd = nodeStart + node.length;
    const from = Math.max(start, nodeStart) - nodeStart;
    const to = Math.min(end, nodeEnd) - nodeStart;
    if (to <= from) continue;

    let target = node;
    if (from > 0) target = target.splitText(from);
    if (to - from < target.length) target.splitText(to - from);

    const mark = document.createElement('mark');
    mark.className = PREVIEW_MARK_CLASS;
    target.parentNode?.insertBefore(mark, target);
    mark.appendChild(target);
  }
}

/**
 * Highlight what changed between the previous render and the one now in
 * `container`, and return one scroll target per changed place, in reading
 * order.
 *
 * A paragraph that replaced another gets its differing characters marked; a
 * paragraph that is new is tinted whole; a deletion tints the paragraph that
 * now stands where the removed text was, so the user is still taken there.
 */
export function highlightPreviewChanges(
  container: HTMLElement,
  previousTexts: string[]
): HTMLElement[] {
  const paragraphs = collectBodyParagraphs(container);
  if (paragraphs.length === 0) return [];
  const texts = paragraphs.map(paragraph => paragraph.textContent);
  const targets: HTMLElement[] = [];

  for (const hunk of diffSequences(previousTexts, texts)) {
    if (hunk.newCount === 0) {
      const neighbour = paragraphs[Math.min(hunk.newStart, paragraphs.length - 1)];
      neighbour.classList.add(PREVIEW_CHANGE_CLASS, PREVIEW_CHANGE_REMOVED_CLASS);
      targets.push(neighbour);
      continue;
    }
    for (let index = 0; index < hunk.newCount; index += 1) {
      const paragraph = paragraphs[hunk.newStart + index];
      paragraph.classList.add(PREVIEW_CHANGE_CLASS);
      if (index < hunk.oldCount) {
        const range = changedCharacterRange(
          previousTexts[hunk.oldStart + index],
          texts[hunk.newStart + index]
        );
        if (range) markTextRange(paragraph, range.start, range.end);
      } else {
        paragraph.classList.add(PREVIEW_CHANGE_ADDED_CLASS);
      }
      if (index === 0) targets.push(paragraph);
    }
  }

  return targets;
}
